/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { FinancialEditPayload } from './EditModal/financialEditHelpers';
import { buildInlineSavePayload } from './inlineSavePayload';
import { Customer } from '../../../store/Customer';
import { EditFieldType, LabelValueField } from './LabelValueField';
import { Asset } from '../../../store/Asset';
import { RpmAccount } from '../../../store/RpmAccount';
import { SupplierMap } from '../../../store/SupplierMap';
import { ApMap } from '../../../store/ApMap';
import { Ticket, TicketStatus } from '../../../store/Ticket';
import { User } from '../../../store/User';
import { ProvisionalAsset } from '../../../store/ProvisionalAsset';
import { CallResult } from '../../../Api/APIService';
import { EntityType } from '../../../store/Search';
import { Link } from '../../../store/Link';
import { ProductMap } from '../../../store/ProductMap';
import { CustomerMap } from '../../../store/CustomerMap';
import { RunTotalAudit } from '../../../store/RunTotalAudit';
import { Payment } from '../../../store/Payment';
import { PaymentRecipient } from '../../../store/PaymentRecipient';
import { Correction } from '../../../store/Correction';
import { Adjustment } from '../../../store/Adjustment';

export interface FieldLabelMap {
  [key: string]: string;
}

export interface CanEdit {
  field: string;
  type: EditFieldType;
  options?: any[];
  default?: any;
  mapType?: EntityType;
  searchField?: string;
  useDefaultWidth?: boolean;
}

export interface Detail {
  label: string;
  value: string | number;
  canEditField?: CanEdit;
  field: string;
  link?: LinkMeta;
}

export interface LinkMeta {
  field: string;
  link: string | null;
  external: boolean;
  externalLink?: ((id: string) => string) | string;
}

export type DetailPanelEntity =
  | Asset
  | RpmAccount
  | Customer
  | SupplierMap
  | Ticket
  | ProvisionalAsset
  | Link
  | RunTotalAudit
  | ApMap
  | ProductMap
  | CustomerMap
  | Payment
  | PaymentRecipient
  | Correction
  | Adjustment;

export interface DetailPanelParams {
  data: DetailPanelEntity;
  canEdit: CanEdit[];
  fieldMap: FieldLabelMap;
  containerClasses?: string;
  order: string[];
  saveAction?: (data: DetailPanelEntity | FinancialEditPayload) => Promise<CallResult<any>>;
  links?: LinkMeta[];
  associationNameFields?: {
    [key: string]: string;
  };
  invalidFields?: string[];
}

export const DetailPanel = ({
  data,
  canEdit,
  fieldMap,
  order,
  saveAction,
  invalidFields,
  links,
  associationNameFields
}: DetailPanelParams) => {
  const [detailData, setDetailData] = useState<Detail[]>([]);

  const formatData = () => {
    const details = order.reduce<Detail[]>((acc, k) => {
      const valueKey = associationNameFields ? associationNameFields[k as keyof typeof associationNameFields] || k : k;
      const val = data[valueKey as keyof typeof data];
      acc.push({
        label: fieldMap[k],
        value: ['assignedTo', 'createdBy'].includes(k)
          ? `${(val as User)?.firstName || ''} ${(val as User)?.lastName}`
          : 'ap_rpm_rep_id' == k
            ? val
              ? `${(data as ApMap)?.ap_rpm_rep_name || ''}, ${(data as ApMap)?.ap_rpm_rep_agency_name || ''}`
              : ''
            : val,
        canEditField: canEdit.find((o) => o.field === k),
        field: k,
        link: links?.find((l) => l.field === k)
      });
      return acc;
    }, []);

    setDetailData(details);
  };

  const handleSave = async (
    newValue: string | number | boolean | null,
    field: string,
    associationNameValue?: string
  ) => {
    if (saveAction) {
      const current = data as Record<string, any>;
      const changes: Record<string, unknown> = {};

      if (field === 'commissionsValidationStatus' && newValue !== current[field]) {
        changes['commissionsValidatedDate'] = new Date().toISOString();
      }
      if (field === 'actualDecommissionedDate' && newValue && newValue !== current[field]) {
        const statusFieldName = 'commissionStatus' in current ? 'commissionStatus' : 'commissionsStatus';
        if (current[statusFieldName] !== 'Legacy Archive') {
          changes[statusFieldName] = 'Decommissioned';
        }
      }
      if (field === 'status') {
        if (newValue === TicketStatus.PENDING_SOURCE_RESPONSE && !current['submittedToSourceDate']) {
          changes['submittedToSourceDate'] = new Date().toISOString();
        } else if (newValue === TicketStatus.RESOLVED && !current['actualResolutionDate']) {
          changes['actualResolutionDate'] = new Date().toISOString();
        }
      }
      changes[field] = newValue;
      const associationNameField =
        associationNameFields && associationNameFields[field as keyof typeof associationNameFields];
      if (associationNameField) {
        changes[associationNameField] = associationNameValue;
      }
      const { error } = await saveAction(buildInlineSavePayload(data, changes));
      if (error) return 'Error saving data';
    }
    return null;
  };

  const handleObjectSave = async (data: Asset | ProvisionalAsset | Link | RpmAccount | FinancialEditPayload) => {
    if (saveAction) return saveAction(data);
  };

  useEffect(() => formatData(), [data]);

  return (
    <>
      {detailData.length ? (
        <div
          className="w-full flex flex-wrap pt-4 px-4 [&:not(:last-child)]:border-b"
          data-testid="detail-panel">
          {detailData.map((d, index) => (
            <LabelValueField
              key={index}
              label={d.label}
              value={d.value}
              canEdit={d.canEditField}
              saveValueAction={(val, previewValue) => handleSave(val, d.field, previewValue)}
              saveObjectAction={(d) => handleObjectSave(d)}
              link={d.link}
              field={d.field}
              data={data}
              isValid={invalidFields ? !invalidFields.includes(d.field) : true}
            />
          ))}
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
